import * as jwtDecode from 'jwt-decode';
import { baseApi } from './baseApi';
import { utf8ToBase64 } from './utils';
import { getValidAccessToken } from './auth/accessTokenManager';
import { constructError } from './resultApi';
import type { AppDispatch, RootState } from '../state';
import { store } from '../state';

// External data passed alongside a query as a temporary ClickHouse table, keyed by table name.
// See https://clickhouse.com/docs/engines/table-engines/special/external-data
interface IExternalTable {
    data: Record<string, unknown>[];
    structure: Record<string, string>;
}

interface IQueryIndexArgs {
    query: string;
    externalTables?: Record<string, IExternalTable>;
}

// Names that would collide with reserved ClickHouse HTTP params ("query", "default_format").
const RESERVED_EXTERNAL_TABLE_NAMES = new Set(['query', 'default']);

export const indexApi = baseApi.injectEndpoints({
    overrideExisting: false,
    endpoints: (build) => ({
        queryIndex: build.mutation<Uint8Array, IQueryIndexArgs>({
            queryFn: async ({ query, externalTables }, api, extraOptions, baseQuery) => {
                if (!query || typeof query !== 'string') {
                    return constructError('Query must be a string');
                }

                const { dispatch, getState } = api;

                try {
                    const accessToken = await getValidAccessToken(dispatch as AppDispatch, getState as () => RootState);

                    const { public_id: userUuid } = jwtDecode(accessToken) as { public_id?: string };
                    if (!userUuid) {
                        return constructError('Missing public_id in access token');
                    }

                    const biolibIndexNormalizedUserUuid = userUuid.replaceAll('-', '_');
                    const basicAuthToken = utf8ToBase64(`biolib_user|${biolibIndexNormalizedUserUuid}:${accessToken}`);

                    const params: Record<string, string> = { default_format: 'JSON' };
                    let body: string | FormData = query;

                    if (externalTables && Object.keys(externalTables).length > 0) {
                        const formData = new FormData();
                        params.query = query;
                        for (const [name, table] of Object.entries(externalTables)) {
                            if (RESERVED_EXTERNAL_TABLE_NAMES.has(name.trim().toLowerCase())) {
                                return constructError(`External table name "${name}" is reserved`);
                            }
                            if (!table.structure || Object.keys(table.structure).length === 0) {
                                return constructError(`External table "${name}" must define a structure`);
                            }
                            if (!Array.isArray(table.data)) {
                                return constructError(`External table "${name}" data must be a list of objects`);
                            }
                            const structure = Object.entries(table.structure)
                                .map(([column, type]) => `${column} ${type}`)
                                .join(', ');
                            const jsonEachRow = table.data.map((row) => JSON.stringify(row)).join('\n');
                            formData.append(name, new Blob([jsonEachRow]), name);
                            params[`${name}_format`] = 'JSONEachRow';
                            params[`${name}_structure`] = structure;
                        }
                        body = formData;
                    }

                    const result = await baseQuery({
                        url: '/proxy/index/',
                        params,
                        method: 'POST',
                        body,
                        headers: { Authorization: `Basic ${basicAuthToken}` },
                        responseHandler: (response) => response.arrayBuffer(),
                    });

                    if (result.error) {
                        return { error: result.error };
                    }

                    return { data: new Uint8Array(result.data as ArrayBuffer) };
                } catch (error) {
                    return constructError(error instanceof Error ? error.message : 'Unknown error');
                }
            },
        }),
    }),
});

export const { useQueryIndexMutation } = indexApi;

export async function queryIndex(payload: IQueryIndexArgs): Promise<Uint8Array> {
    // @ts-ignore
    const result = await store.dispatch(indexApi.endpoints.queryIndex.initiate(payload));

    if (result.error) {
        const errorMessage =
            'error' in result.error
                ? result.error.error
                : 'status' in result.error
                  ? `Query failed with status ${result.error.status}`
                  : (result.error.message ?? 'Query failed');
        throw new Error(errorMessage as string);
    }

    return result.data as Uint8Array;
}
